import { Component, h, Method, Prop, State } from '@stencil/core';
import { Shipping, ShippingPriceType, StepItem } from '../../../redux/types';
import { store } from '../../../redux/store';
import { Unsubscribe } from '@stencil/redux';
import { changeShippingAddress, fetchGeoCode, initForOrder, markStepAsLoaded, prepareShippingStep, selectShippingMethod } from '../../../redux/actions';
import { RootState } from '../../../redux/reducers';
import { Coordinates, StepErrorType } from '../../../types/types';
import { t } from '../../../redux/utils';

@Component({
  tag: 'rentware-shipping-step',
  styleUrl: 'style.css',
  shadow: true,
})
export class RentwareShippingStep {
  @Prop({ mutable: true }) shippingState: Shipping;
  @Prop({ mutable: true }) cartState: any;
  @Prop() removeError;
  @Prop() currentStep: StepItem;
  @Prop() errors;

  @State() typedAddress: string = '';
  @State() openAddressHint: boolean = false;
  @State() foundAddressHint: string = '';
  @State() chosenCoordinates: Coordinates;
  @State() waitingForCalculation: boolean = false;

  templateRef;

  @Method()
  async goToFirstError() {
    this.templateRef.goToFirstError();
  }

  prepareShippingStep!: typeof prepareShippingStep;
  selectDeliveryOption!: typeof selectShippingMethod;
  initForOrder!: typeof initForOrder;
  fetchGeoCode!: typeof fetchGeoCode;
  unsubscribe!: Unsubscribe;
  markStepAsLoaded!: typeof markStepAsLoaded;
  changeShippingAddress!: typeof changeShippingAddress;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        shippingState: state.shipping,
        cartState: state.cart,
      };
    });

    store.mapDispatchToProps(this, {
      prepareShippingStep,
      selectDeliveryOption: selectShippingMethod,
      initForOrder,
      fetchGeoCode,
      markStepAsLoaded,
      changeShippingAddress,
    });

    if (this.shippingState?.shippingAddress) {
      this.setWaiting();

      this.fetchGeoCode(
        this.shippingState.shippingAddress,
        data => {
          this.foundAddressHint = data.placeName;
          this.chosenCoordinates = { latitude: data.lat, longitude: data.long };
          this.changeShippingAddress(this.foundAddressHint);
          this.prepareShippingStep(
            this.chosenCoordinates,
            this.foundAddressHint,
            () => {
              const currentOption = this.shippingState.options.find(option => option.id === this.shippingState.selectedMethodId);
              this.handleOptionChange(null, currentOption);
              this.setActive();
            },
            this.setActive,
          );
        },
        this.setActive,
      );
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  componentDidLoad() {
    this.prepareShippingStep();
    this.markStepAsLoaded(this.currentStep.id, this.currentStep.visitContext);
  }

  handleOptionChange = (e, item) => {
    e?.preventDefault();

    this.removeError(this.currentStep.id, StepErrorType.ShippingNoOptionSelected);
    if (item.grossPrice > 0 || item.priceType === ShippingPriceType.Fixed) {
      this.removeError(this.currentStep.id, StepErrorType.ShippingDistanceProblem);
    }

    this.selectDeliveryOption(item, () => this.initForOrder(this.setActive), undefined);
  };

  handleAddressInput = (typedAddress: string) => {
    this.typedAddress = typedAddress;

    if (this.typedAddress.length > 4) {
      this.fetchGeoCode(typedAddress, data => {
        this.foundAddressHint = data.placeName;
        this.chosenCoordinates = { latitude: data.lat, longitude: data.long };
        this.openAddressHint = true;
      });
    }
  };

  handleHintClick = () => {
    this.setWaiting();
    this.changeShippingAddress(this.foundAddressHint);
    this.closeHint();
    this.prepareShippingStep(this.chosenCoordinates, this.foundAddressHint, () => {
      const currentOption = {
        ...this.shippingState.options.find(option => option.id === this.shippingState.selectedMethodId),
        address: this.foundAddressHint,
      };
      this.handleOptionChange(null, currentOption);
      this.setActive();
    });
  };

  closeHint = () => {
    this.openAddressHint = false;
  };

  setWaiting = () => {
    this.waitingForCalculation = true;
  };

  setActive = () => {
    this.waitingForCalculation = false;
  };

  get options() {
    return this.shippingState.options.map(item => {
      const key = item.id === 'pickUp' ? 'checkout.deliveryPickUpTitle' : 'shipping.shippingOptionName:' + item.id;
      const name = key ? t(key) : item.name;

      return {
        ...item,
        name: name,
      };
    });
  }

  render() {
    if (!this.shippingState) {
      return null;
    }

    return (
      <rentware-checkout-shipping-template
        options={this.options}
        selectedOption={this.shippingState.selectedMethodId}
        onOptionChange={this.handleOptionChange}
        errors={this.errors}
        currentStep={this.currentStep}
        typedAddress={this.typedAddress}
        addressFieldDescription={t('checkout.deliveryAddressFieldDescription')}
        addressFieldDescriptionSuccess={t('checkout.deliveryAddressFieldSuccess')}
        addressHint={this.foundAddressHint}
        addressInInput={this.shippingState.shippingAddress}
        pickOptionText={t('checkout.pickDeliveryOption')}
        enterAddressText={t('checkout.deliveryEnterAddressOption')}
        onAddressInput={this.handleAddressInput}
        onHintClick={this.handleHintClick}
        addressLabel={t('checkout.deliveryAddressLabel')}
        openAddressHint={this.openAddressHint}
        ref={el => (this.templateRef = el)}
        waitingForCalculation={this.waitingForCalculation}
        closeHint={this.closeHint}
      />
    );
  }
}
